/**
 * Vuex module for menu access control
 * Fetches and manages user's allowed menu items based on role permissions
 */
import apiClient from '../../services/api'

export const state = {
    userMenus: [],                // Hierarchical menu tree
    flatMenuIds: [],             // Flat array of allowed menu IDs
    loading: false,
    error: null,
    lastFetched: null,
    cacheTimeout: 5 * 60 * 1000  // 5 minutes cache
}

export const mutations = {
    SET_USER_MENUS(state, { data, flatMenuIds }) {
        state.userMenus = data || []
        state.flatMenuIds = flatMenuIds || []
        state.lastFetched = new Date().getTime()
        state.error = null
    },

    SET_LOADING(state, loading) {
        state.loading = loading
    },

    SET_ERROR(state, error) {
        state.error = error
        state.loading = false
    },

    CLEAR_MENUS(state) {
        state.userMenus = []
        state.flatMenuIds = []
        state.lastFetched = null
        state.error = null
    }
}

export const getters = {
    /**
     * Check if user can access a specific menu item
     * @param {string} menuId - Menu item ID (e.g., 'hwc.counties')
     * @returns {boolean}
     */
    canAccessMenu: (state) => (menuId) => {
        return state.flatMenuIds.includes(menuId)
    },

    /**
     * Get menu tree for rendering
     */
    menuTree(state) {
        return state.userMenus
    },

    /**
     * Check if menus are loaded
     */
    menusLoaded(state) {
        return state.userMenus.length > 0
    },

    /**
     * Check if cache is valid
     */
    isCacheValid(state) {
        if (!state.lastFetched) return false
        const now = new Date().getTime()
        return (now - state.lastFetched) < state.cacheTimeout
    }
}

export const actions = {
    /**
     * Fetch user's allowed menus from API
     * Uses cache if available and valid
     */
    async fetchUserMenus({ commit, getters }, { force = false } = {}) {
        // Return cached data if valid and not forcing refresh
        if (!force && getters.isCacheValid && getters.menusLoaded) {
            console.log('[MenuAccess] Using cached menus')
            return Promise.resolve(getters.menuTree)
        }

        commit('SET_LOADING', true)

        try {
            console.log('[MenuAccess] Fetching user menus from API')
            const response = await apiClient.get('/admin-menu/user-menus')

            if (response.data.success) {
                commit('SET_USER_MENUS', {
                    data: response.data.data,
                    flatMenuIds: response.data.flat_menu_ids || []
                })
                console.log('[MenuAccess] Menus loaded:', response.data.data.length, 'root items')
                return response.data.data
            } else {
                throw new Error(response.data.message || 'Failed to fetch menus')
            }
        } catch (error) {
            console.error('[MenuAccess] Error fetching menus:', error)

            // If unauthorized, clear menus
            if (error.response && error.response.status === 401) {
                commit('CLEAR_MENUS')
            } else {
                commit('SET_ERROR', error.message || 'Failed to load menus')
            }

            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    /**
     * Clear menu cache (e.g., on logout)
     */
    clearMenus({ commit }) {
        console.log('[MenuAccess] Clearing menu cache')
        commit('CLEAR_MENUS')
    },

    /**
     * Refresh menus (force reload from API)
     */
    async refreshMenus({ dispatch }) {
        console.log('[MenuAccess] Forcing menu refresh')
        return dispatch('fetchUserMenus', { force: true })
    },

    /**
     * Initialize module - called on app startup
     */
    async init({ dispatch, rootGetters }) {
        // Only fetch if user is logged in
        if (rootGetters['auth/loggedIn']) {
            try {
                await dispatch('fetchUserMenus')
            } catch (error) {
                console.error('[MenuAccess] Failed to initialize menus:', error)
            }
        }
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions
}
